import { Weekday } from '@capacitor/local-notifications';
import { BusinessHoursResponseDto } from '@tiendanube/common';
import { GroupedBusinessHours } from './PickupPointItem';

export const getWeekdayValue = (day: string): number => {
  const normalizedDay =
    day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
  return Weekday[normalizedDay as keyof typeof Weekday];
};

const areTimeRangesEqual = (
  ranges1: { start: string; end: string }[],
  ranges2: { start: string; end: string }[],
): boolean => {
  if (ranges1.length !== ranges2.length) return false;

  const sorted1 = [...ranges1].sort((a, b) => a.start.localeCompare(b.start));
  const sorted2 = [...ranges2].sort((a, b) => a.start.localeCompare(b.start));

  return sorted1.every(
    (range, index) =>
      range.start === sorted2[index].start && range.end === sorted2[index].end,
  );
};

const areDaysSequential = (day1: string, day2: string): boolean => {
  const day1Value = getWeekdayValue(day1);
  const day2Value = getWeekdayValue(day2);

  return day2Value - day1Value === 1 || (day1Value === 6 && day2Value === 0);
};

export const groupBusinessHours = (
  businessHours: BusinessHoursResponseDto[],
): GroupedBusinessHours[] => {
  const hoursByDay: Record<string, { start: string; end: string }[]> = {};

  for (const { day, start, end } of businessHours) {
    if (!hoursByDay[day]) {
      hoursByDay[day] = [];
    }
    hoursByDay[day].push({ start, end });
  }

  for (const hours of Object.values(hoursByDay)) {
    hours.sort((a, b) => a.start.localeCompare(b.start));
  }

  const sortedDays = Object.keys(hoursByDay).sort(
    (a, b) => getWeekdayValue(a) - getWeekdayValue(b),
  );

  const result: GroupedBusinessHours[] = [];
  let currentGroup: GroupedBusinessHours | null = null;

  for (const day of sortedDays) {
    const dayHours = hoursByDay[day];

    if (!currentGroup) {
      currentGroup = {
        days: [day],
        hours: dayHours,
      };
      continue;
    }

    const lastDay = currentGroup.days[currentGroup.days.length - 1];

    if (
      areDaysSequential(lastDay, day) &&
      areTimeRangesEqual(currentGroup.hours, dayHours)
    ) {
      currentGroup.days.push(day);
    } else {
      result.push(currentGroup);
      currentGroup = {
        days: [day],
        hours: dayHours,
      };
    }
  }

  if (currentGroup) {
    result.push(currentGroup);
  }

  return result;
};
